<template>
  <div v-if="steps && steps.length > 0" class="w-full py-1 flex justify-start md:justify-center">
    <div class="inline-flex justify-start md:justify-center items-start">
      <div v-for="(step, index) in steps" :key="index" class="flex flex-col items-center relative" :style="{ width: '110px' }">
        <div v-if="index < steps.length - 1" class="absolute top-3 left-[calc(50%+12px)] h-0.5 z-0" :style="{ width: 'calc(100% - 24px)', right: 'auto' }">
          <div class="w-full h-0.5 bg-gray-200 dark:bg-gray-700 relative overflow-hidden">
            <div class="h-full transition-all duration-500" :class="getConnectorClass(index)" :style="{ width: index < currentStepIndex ? '100%' : (index === currentStepIndex ? '50%' : '0%') }"></div>
          </div>
        </div>
        
        <div class="relative z-10">
          <div class="w-6 h-6 rounded-full flex items-center justify-center transition-all duration-300 border" :class="getStepCircleClass(index)">
            <UIcon :name="getStepIcon(index)" class="w-3 h-3" :class="index <= currentStepIndex ? 'text-white' : 'text-gray-400 dark:text-gray-500'" />
          </div>
          <div v-if="index === currentStepIndex && !isRejected" class="absolute inset-0 rounded-full animate-ping bg-blue-500 opacity-30"></div>
        </div>
        
        <div class="mt-1 text-center px-0.5 w-full">
          <span class="text-[11px] font-medium leading-tight block break-words" :class="getStepTextClass(index)">
            {{ getStepLabel(step.type) }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { resolveComponent, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import type { ApprovalStep, WorkflowStatus } from '@/api/eOrdersApi'
import { WorkflowStatus as WorkflowStatusEnum } from '@/api/eOrdersApi'

const UIcon = resolveComponent('UIcon')
const { t } = useI18n()

const props = defineProps<{
  steps: ApprovalStep[] | undefined;
  currentStepIndex: number | undefined;
  status: WorkflowStatus | undefined;
}>()

const isRejected = computed(() => props.status === WorkflowStatusEnum.QuotationRejection || props.status === WorkflowStatusEnum.Rejected)
const currentStepIndex = computed(() => props.currentStepIndex ?? -1)

const getStepLabel = (type: string) => {
  const camelCaseType = type.charAt(0).toLowerCase() + type.slice(1);
  
  return t(`workflow.approval.stepTypes.${camelCaseType}`, type);
}

const getStepCircleClass = (index: number) => {
  if (index < currentStepIndex.value) {
    return 'bg-green-500 border-green-500'
  } else if (index === currentStepIndex.value) {
    return isRejected.value ? 'bg-red-500 border-red-500 shadow-lg' : 'bg-blue-500 border-blue-500 shadow-lg'
  }
  
  return 'bg-(--ui-bg-elevated) border-gray-300 dark:border-gray-600'
}

const getStepTextClass = (index: number) => {
  if (index < currentStepIndex.value) {
    return 'text-green-700 dark:text-green-400'
  } else if (index === currentStepIndex.value) {
    return isRejected.value ? 'text-red-700 dark:text-red-400' : 'text-blue-700 dark:text-blue-400'
  }
  
  return 'text-gray-500 dark:text-gray-400'
}

const getConnectorClass = (index: number) => {
  if (index < currentStepIndex.value) {
    return 'bg-gradient-to-r from-green-500 via-green-400 to-green-300 dark:from-green-600 dark:via-green-500 dark:to-green-400'
  } else if (index === currentStepIndex.value && !isRejected.value) {
    return 'bg-gradient-to-r from-blue-500 via-blue-400 to-blue-200 dark:from-blue-600 dark:via-blue-500 dark:to-blue-300'
  }
  
  return ''
}

const getStepIcon = (index: number) => {
  if (index < currentStepIndex.value) {
    return 'i-mdi-check'
  } else if (index === currentStepIndex.value) {
    return isRejected.value ? 'i-mdi-close' : 'i-mdi-account-clock'
  }
  
  return 'i-mdi-clock-outline'
}
</script>
